import * as React from "react";

import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Icon } from "../../components/ui/icon";
import { Input } from "../../components/ui/input";
import {
  Popover,
  PopoverPopup,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Separator } from "../../components/ui/separator";
import { Text } from "../../components/ui/text";
import type { Omittable } from "../../lib/props";
import {
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorFooter,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  useSelectorKeyboardNav,
  type EntityItemConfig,
  type EntitySelection,
  type EntitySelectorConfig,
  type EntitySelectorVariant,
} from "../entity-selector";

/**
 * THE creatable-vocabulary picker: one search-or-add field, plain-text rows
 * that pick and close, a row that clears the value, a live Create row for a
 * typed name, and a hover "…" rename/delete editor on the rows a workspace
 * added itself.
 *
 * Fixed and manageable vocabulary render as ONE scrolling list — fixed rows
 * first, manageable rows after them. Without `manageableLabel` nothing sits
 * between them: a word someone adds is not a different kind of word, and the
 * "…" editor alone tells the populations apart. With it, the manageable rows
 * group under an in-list group heading — the group boundary is what explains why
 * those rows and no others carry the editor. Which rows are manageable, and
 * which of THOSE this viewer may edit (`editable`), is the consumer's call.
 *
 * The list wears no sticky pool label. The search field names what the rows
 * are, and a group heading pinned over a one-pool list of words only costs a row
 * of height; the pool keeps its label for the listbox's accessible name.
 *
 * Presentation only — the vocabulary and every write stay with the consumer,
 * which is also why the pick reports the whole option rather than an id: the
 * value these lists back is usually the WORD, not a key.
 */

/** The pool holding the whole vocabulary. There is only ever one. */
const POOL_ID = "options";

/**
 * Ids for the two rows that are acts rather than words. Prefixed so they can
 * never collide with a consumer's option id, and intercepted before either can
 * reach a selection.
 */
const CREATE_ROW_ID = "creatable-option-picker:create";
const CLEAR_ROW_ID = "creatable-option-picker:clear";

/**
 * The clear row's accessible name. What the eye reads is the em dash every
 * empty value wears (`CLEAR_ROW_MARK`); a bare dash announces as punctuation
 * ("em dash") where the row's whole job is to name the absence of a value, so
 * the word stays the row's name for search and assistive tech.
 */
const CLEAR_ROW_LABEL = "None";
const CLEAR_ROW_MARK = "—";

/**
 * The list's height cap. Short enough that a long vocabulary still reads as a
 * dropdown attached to its trigger rather than a panel of its own.
 */
const LIST_MAX_HEIGHT = 240;

/** One pickable row. `id` is the consumer's identity for it — a stored row's
 *  id, or the word itself for vocabulary that ships as bare strings. */
export type CreatableOption = {
  id: string;
  label: string;
  /** A quiet second line under the word — a standing fact about it ("Default")
   *  rather than a second name; it takes no part in search or identity. */
  description?: string;
  /** For a `manageableOptions` row: whether THIS viewer may rename/delete it.
   *  `false` keeps the row in the manageable group (it is still a word the
   *  workspace added) but withholds the "…" editor. @default true */
  editable?: boolean;
};

/** Case-insensitive, whitespace-tolerant identity, so "no answer" resolves to
 *  the existing "No answer" instead of minting a twin. */
export function creatableOptionKey(label: string): string {
  return label.trim().toLowerCase();
}

export type CreatableOptionPickerProps = Omittable<{
  /** Fixed vocabulary: up top, never renamed or deleted from here. */
  options: CreatableOption[];
  /** Manageable rows — same list, after the fixed rows, and the only ones that
   *  wear the "…" editor (when rename/delete handlers are passed, and the row
   *  is not `editable: false`). */
  manageableOptions?: CreatableOption[];
  /** An in-list group heading over the manageable rows ("Custom"). Omit to run the
   *  two populations together with nothing between them. */
  manageableLabel?: string;
  /** The applied option's id. `null` checks nothing. */
  selectedId: string | null;
  onPick: (option: CreatableOption) => void;
  /** Omit to drop the clear row — for a picker whose value is required. */
  onClear?: () => void;
  /** Add the typed name to the vocabulary AND apply it; the picker only closes
   *  and clears its search. Never called with a name already in the list.
   *  Omit to drop the Create row — a governed vocabulary the viewer cannot
   *  grow still searches and picks. */
  onCreate?: (label: string) => void;
  /** Omit all three to make every row fixed (no "…" affordance anywhere). */
  onRename?: (id: string, label: string) => void;
  onDelete?: (id: string) => void;
  /** An option whose edit is more than a rename — a word that also carries a
   *  tone, a color, a policy — opens the host's own form instead of the
   *  inline rename field: the "…" editor offers one "Edit…" row that reports
   *  the option and closes. Pass this OR `onRename`; with both the inline
   *  field wins and this is ignored. */
  onEdit?: (option: CreatableOption) => void;
  /** A row under the list, outside the scroll — a link to where the whole
   *  vocabulary is governed ("Manage outcomes"), never another option. */
  footer?: React.ReactNode;
  /** Floors the list at the trigger's width, for a picker standing in a form
   *  as a full-width field: the fixed 280px rung under a 464px control read
   *  as a different instrument. A minimum, never a match — a narrow trigger
   *  still gets the standard rung. @default false */
  fit?: boolean;
  /** Singular, capitalized as it should read in "Edit x" / "x name". */
  noun: string;
  /** Only when `${noun}s` is wrong. */
  plural?: string;
  searchPlaceholder: string;
  align?: "start" | "center" | "end";
  /** Controlled open — for a host that must open the list itself (a form that
   *  answers a submit with a missing required value by opening its picker).
   *  Omit both to stay self-managed. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** True while the vocabulary is still being fetched: holds the empty
   *  state back, since an unfetched list is not an empty one. */
  loading?: boolean;
  /**
   * `inline` mounts the list without a popover of its own, for a host that
   * already owns the popup (a table cell's popover). No trigger renders, the
   * list is always open, and every pick still reports through `onOpenChange`
   * with `false` so the host can close. Unset, the selector's own default
   * applies: `drawer` on a phone (a bottom sheet whose search lifts it to
   * the top under the keyboard), `popover` elsewhere.
   */
  variant?: EntitySelectorVariant;
  /** The pressable that opens the list, so the same list serves a compact
   *  property row and a full-width field in a form. The picker wires it as
   *  the trigger. */
  children?: React.ReactElement<Record<string, unknown>>;
  /** A trigger the host has ALREADY composed on `EntitySelectorTrigger` — a
   *  shared value-chip trigger, say — rendered as-is. Pass this OR
   *  `children`, never both. */
  trigger?: React.ReactNode;
}>;

/** Stable identities, so an omitted list cannot re-seed the memos each render. */
const NO_OPTIONS: CreatableOption[] = [];
const NO_SELECTION: EntitySelection[] = [];

export function CreatableOptionPicker({
  align = "start",
  children,
  fit = false,
  footer,
  loading = false,
  manageableLabel,
  manageableOptions,
  noun,
  onClear,
  onCreate,
  onDelete,
  onEdit,
  onOpenChange,
  onPick,
  onRename,
  open,
  options,
  plural,
  searchPlaceholder,
  selectedId,
  trigger,
  variant,
}: CreatableOptionPickerProps): React.ReactElement {
  // The picker owns the query rather than letting the selector keep it: the
  // Create row's offer is computed from the whole vocabulary, not from what
  // survived the filter, and the clear row is withheld while a search is live.
  const [search, setSearch] = React.useState("");
  const { ref, inputProps } = useSelectorKeyboardNav();

  const query = search.trim();
  const manageable = manageableOptions ?? NO_OPTIONS;
  const canManage = !!onRename || !!onDelete || !!onEdit;
  const pluralNoun = plural ?? `${noun}s`;

  const items = React.useMemo<EntityItemConfig[]>(() => {
    const row = (
      option: CreatableOption,
      editable: boolean,
      section: string | undefined,
    ): EntityItemConfig => ({
      id: option.id,
      title: option.label,
      ...(section ? { section } : {}),
      ...(option.description ? { description: option.description } : {}),
      ...(editable
        ? {
            trailing: () => (
              <OptionRowEditor
                noun={noun}
                onDelete={onDelete}
                onEdit={onEdit}
                onRename={onRename}
                option={option}
              />
            ),
          }
        : {}),
    });
    const rows: EntityItemConfig[] = [];
    // Clearing is an act over the whole vocabulary, not one of the things
    // being searched for: once a query narrows the list the row is withheld
    // rather than left competing with the matches. It heads the list but
    // SCROLLS with it — a row pinned above the words would read as a second
    // field under the search band.
    if (onClear && !query) {
      rows.push({
        actionRow: true,
        body: () => (
          <Text size="sm" variant="muted">
            {CLEAR_ROW_MARK}
          </Text>
        ),
        id: CLEAR_ROW_ID,
        title: CLEAR_ROW_LABEL,
      });
    }
    for (const option of options) rows.push(row(option, false, undefined));
    for (const option of manageable) {
      rows.push(
        row(option, canManage && option.editable !== false, manageableLabel),
      );
    }
    return rows;
  }, [
    canManage,
    manageable,
    manageableLabel,
    noun,
    onClear,
    onDelete,
    onEdit,
    onRename,
    options,
    query,
  ]);

  // Creating is offered only when NOTHING in the vocabulary already carries the
  // typed name. The comparison is the tolerant one, so "no answer" resolves to
  // the existing "No answer" instead of offering to mint a twin beside it.
  const exists = React.useMemo(() => {
    if (!query) return false;
    const key = creatableOptionKey(query);
    return [...options, ...manageable].some(
      (option) => creatableOptionKey(option.label) === key,
    );
  }, [manageable, options, query]);

  // The Create offer is the one pinned row: it is the answer to the query
  // being typed, so it sits at the head of the cursor rather than wherever the
  // filtered list would have put it.
  const pinnedItems = React.useMemo<EntityItemConfig[]>(() => {
    if (!onCreate || !query || exists) return [];
    return [
      {
        actionRow: true,
        id: CREATE_ROW_ID,
        // The typed name leads, because it is the part that must survive the
        // row's truncation — the surrounding field already says what is being
        // named.
        keywords: [query],
        title: `Create “${query}”`,
      },
    ];
  }, [exists, onCreate, query]);

  const config = React.useMemo<EntitySelectorConfig>(
    () => ({
      emptyMessage: `No matching ${pluralNoun.toLowerCase()}`,
      // These rows are words, not named things: the entity weight would read a
      // vocabulary as a list of proper nouns.
      itemTitleWeight: "normal",
      maxSelections: 1,
      noItemsMessage: onCreate
        ? `No ${pluralNoun.toLowerCase()} yet — type to add one`
        : `No ${pluralNoun.toLowerCase()} yet`,
      pools: [
        {
          // Plain text rows, so there is no visual column to reserve — a
          // monogram circle beside every word would read as a roster of people.
          hideAvatars: true,
          id: POOL_ID,
          items,
          label: pluralNoun,
          loading,
          pinnedItems,
          // The manageable run is one named group inside one vocabulary:
          // headed like the pool label (sentence case, air above), never
          // the uppercased group heading with a rule that subdivides a many-group
          // list.
          sectionHeadings: "label",
        },
      ],
      searchPlaceholder,
    }),
    [items, loading, onCreate, pinnedItems, pluralNoun, searchPlaceholder],
  );

  // The applied value lives with the consumer, so the selector holds a mirror of
  // it rather than a selection of its own — which is what puts the check on the
  // current row and homes the cursor there when the list opens.
  const selections = React.useMemo<EntitySelection[]>(() => {
    const picked = [...options, ...manageable].find(
      (option) => option.id === selectedId,
    );
    if (!picked) return NO_SELECTION;
    return [
      {
        entity: { id: picked.id, title: picked.label },
        id: picked.id,
        poolId: POOL_ID,
      },
    ];
  }, [manageable, options, selectedId]);

  const handleBeforeSelect = React.useCallback(
    (entity: EntityItemConfig) => {
      if (entity.id === CREATE_ROW_ID) {
        onCreate?.(query);
      } else if (entity.id === CLEAR_ROW_ID) {
        onClear?.();
      } else {
        // The row IS the option: its id and title were built from one, so
        // reporting them back needs no lookup that could miss.
        onPick({ id: entity.id, label: entity.title });
      }
      // An inline mount has no open flag of its own to flip, so the close the
      // popover branch performs on itself is reported to the host here.
      if (variant === "inline") onOpenChange?.(false);
      // Every press is vetoed. The selector never accumulates a selection of
      // its own — `selectedId` is the single source of what is applied — and
      // the veto is also what closes the list on a re-pick of the current row,
      // where a state that does not change reports nothing.
      return false;
    },
    [onClear, onCreate, onOpenChange, onPick, query, variant],
  );

  return (
    <EntitySelector
      config={config}
      onBeforeSelect={handleBeforeSelect}
      onOpenChange={onOpenChange}
      onSearchChange={setSearch}
      open={open}
      ref={ref}
      search={search}
      title={noun}
      value={selections}
      variant={variant}
    >
      {variant === "inline"
        ? null
        : (trigger ??
          (children ? <EntitySelectorTrigger render={children} /> : null))}
      <EntitySelectorContent
        align={align}
        fit={fit}
        maxHeight={LIST_MAX_HEIGHT}
      >
        <EntitySelectorInput {...inputProps} />
        {/* The search field already says what the rows are; the pool's label
            is kept for the listbox's accessible name only. */}
        <EntitySelectorPool hideLabel value={POOL_ID} />
        <EntitySelectorEmpty />
        {footer ? <EntitySelectorFooter>{footer}</EntitySelectorFooter> : null}
      </EntitySelectorContent>
    </EntitySelector>
  );
}

/**
 * The "…" affordance on a manageable row. It stays mounted rather than
 * unmounting on pointer-leave, so an open editor keeps the anchor it was
 * positioned against when the pointer travels to it.
 */
function OptionRowEditor({
  noun,
  onDelete,
  onEdit,
  onRename,
  option,
}: {
  noun: string;
  onDelete: ((id: string) => void) | undefined;
  onEdit: ((option: CreatableOption) => void) | undefined;
  onRename: ((id: string, label: string) => void) | undefined;
  option: CreatableOption;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger
        render={
          <Button
            aria-label={`Edit ${option.label}`}
            shape="square"
            size="icon-xs"
            // A pointer shortcut inside an option: out of the tab order, as
            // the row's other hover chrome is (see EntitySelectorItem).
            tabIndex={-1}
            variant="ghost"
          >
            <Icon color="muted" name="ellipsis" size="sm" />
          </Button>
        }
      />
      {/* The trigger sits at the row's right edge, so the editor opens back
          over the list rather than off the far side of the screen. */}
      <PopoverPopup align="end" side="bottom" width="card">
        {/* Mounting only while open is what reseeds the draft name and the
            delete confirmation on every visit. */}
        {open ? (
          <OptionEditor
            close={() => setOpen(false)}
            noun={noun}
            onDelete={onDelete}
            onEdit={onEdit}
            onRename={onRename}
            option={option}
          />
        ) : null}
      </PopoverPopup>
    </Popover>
  );
}

/** Rename field (or an "Edit…" row that hands off to the host's form) +
 *  two-step delete. */
function OptionEditor({
  close,
  noun,
  onDelete,
  onEdit,
  onRename,
  option,
}: {
  close: () => void;
  noun: string;
  onDelete: ((id: string) => void) | undefined;
  onEdit: ((option: CreatableOption) => void) | undefined;
  onRename: ((id: string, label: string) => void) | undefined;
  option: CreatableOption;
}): React.ReactElement {
  const [draft, setDraft] = React.useState(option.label);
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);

  // Blur commits as well as Enter: the delete button is one of the things a
  // person tabs to, and a rename abandoned on the way there is a lost edit.
  const commitRename = (): void => {
    const next = draft.trim();
    if (next && next !== option.label) onRename?.(option.id, next);
  };

  return (
    <Box direction="column" gap="sm">
      {onRename ? (
        <Input
          // The placeholder is not a label: it names the field only until the
          // first keystroke, and this one opens already holding a value.
          aria-label={`${noun} name`}
          autoFocus
          onBlur={commitRename}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            commitRename();
            close();
          }}
          placeholder={`${noun} name`}
          size="sm"
          value={draft}
        />
      ) : onEdit ? (
        <Button
          className="justify-start"
          onClick={() => {
            onEdit(option);
            close();
          }}
          shape="square"
          size="sm"
          variant="ghost"
        >
          <Icon color="muted" name="pen-line" size="sm" />
          <Text size="sm">Edit {noun.toLowerCase()}…</Text>
        </Button>
      ) : null}
      {(onRename || onEdit) && onDelete ? <Separator /> : null}
      {onDelete ? (
        confirmingDelete ? (
          <Box align="center" direction="row" gap="sm" justify="end">
            <Button
              onClick={() => setConfirmingDelete(false)}
              size="sm"
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                onDelete(option.id);
                close();
              }}
              size="sm"
              color="destructive"
            >
              Delete {noun.toLowerCase()}
            </Button>
          </Box>
        ) : (
          <Button
            className="justify-start"
            onClick={() => setConfirmingDelete(true)}
            shape="square"
            size="sm"
            variant="ghost"
          >
            <Icon color="muted" name="trash" size="sm" />
            <Text size="sm">
              Delete
            </Text>
          </Button>
        )
      ) : null}
    </Box>
  );
}
